/**
 * AttachmentsManager — gestor genérico de adjuntos (fotos / documentos) para
 * cualquier módulo cuyo backend exponga endpoints REST de archivos:
 *
 *   GET    {apiBase}/{id}/archivos          → lista (URLs firmadas)
 *   POST   {apiBase}/{id}/archivos          → sube un archivo (multipart `file`)
 *   DELETE {apiBase}/{id}/archivos?key=...  → borra un archivo
 *
 * Lo usan Agenda de contactos (antes "Agenda de maestros") y Costos sugeridos.
 * Cumple la convención de UX del
 * proyecto (drag & drop + click + Ctrl+V) reusando `<DragDropUpload>` y
 * `useClipboardPaste`. Ver docs/storage-y-visor.md §7.1.
 *
 * Si `entityId` es null (el registro aún no se guardó), muestra un aviso
 * pidiendo guardar primero — evita el problema de "subir a un borrador sin id".
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Empty,
  Image as AntImage,
  Modal as AntModal,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FilePptOutlined,
  FileTextOutlined,
  FileWordOutlined,
  FileZipOutlined,
  PlayCircleOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { DragDropUpload, useClipboardPaste } from "./DragDropUpload";
import { apiFetch } from "../services/apiClient";
import { getMsg } from "../utils/antdStatic";
import { descargarVerificado } from "../utils/descargaArchivo";
import VideoPlayer from "./VideoPlayer";

const { Text } = Typography;

/** Metadatos de archivo + URLs firmadas, tal como los devuelve el backend
 *  (`storageService.attachUrls`). */
export interface AttachmentFile {
  provider?: string;
  key: string;
  thumbKey?: string | null;
  originalName: string;
  mimeType: string;
  size: number;
  kind: "image" | "video" | "doc" | string;
  url: string;
  thumbUrl?: string | null;
  /** URL firmada de la versión liviana del video (480p); `null` si no existe en disco. */
  previewUrl?: string | null;
  /** Peso (bytes) de la versión liviana, si existe. */
  previewSize?: number | null;
}

// MIME aceptados — mantener sincronizado con utils/attachmentsMulter.js (backend).
export const ATTACHMENTS_ACCEPT =
  "image/jpeg,image/png,image/webp,image/gif,image/bmp," +
  "video/mp4,video/quicktime,video/webm,video/x-msvideo,video/x-matroska,video/3gpp,video/3gpp2," +
  "application/pdf," +
  "application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document," +
  "application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet," +
  "application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation," +
  "text/plain,text/csv";

const MAX_UPLOAD_BYTES = 100 * 1024 * 1024; // 100 MB (coincide con multer)

function formatBytes(bytes: number): string {
  if (!bytes || bytes < 1) return "—";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return i === 0 ? `${Math.round(value)} ${units[i]}` : `${value.toFixed(1)} ${units[i]}`;
}

function FileIcon({ file, size = 40 }: { file: AttachmentFile; size?: number }) {
  const mime = file.mimeType || "";
  const style = { fontSize: size, color: "#8c8c8c" };
  if (file.kind === "image")
    return <FileImageOutlined style={{ ...style, color: "#13c2c2" }} />;
  if (file.kind === "video")
    return <VideoCameraOutlined style={{ ...style, color: "#722ed1" }} />;
  if (mime === "application/pdf")
    return <FilePdfOutlined style={{ ...style, color: "#cf1322" }} />;
  if (mime.includes("word"))
    return <FileWordOutlined style={{ ...style, color: "#1677ff" }} />;
  if (mime.includes("excel") || mime.includes("spreadsheet"))
    return <FileExcelOutlined style={{ ...style, color: "#52c41a" }} />;
  if (mime.includes("powerpoint") || mime.includes("presentation"))
    return <FilePptOutlined style={{ ...style, color: "#fa8c16" }} />;
  if (mime.includes("zip"))
    return <FileZipOutlined style={{ ...style, color: "#faad14" }} />;
  if (mime.startsWith("text/"))
    return <FileTextOutlined style={{ ...style, color: "#595959" }} />;
  return <FileOutlined style={style} />;
}

function kindLabel(file: AttachmentFile): { text: string; color: string } {
  if (file.kind === "image") return { text: "Imagen", color: "cyan" };
  if (file.kind === "video") return { text: "Video", color: "purple" };
  const mime = file.mimeType || "";
  if (mime === "application/pdf") return { text: "PDF", color: "red" };
  if (mime.includes("word")) return { text: "Word", color: "blue" };
  if (mime.includes("excel") || mime.includes("spreadsheet"))
    return { text: "Excel", color: "green" };
  if (mime.includes("powerpoint") || mime.includes("presentation"))
    return { text: "PPT", color: "orange" };
  if (mime.startsWith("text/")) return { text: "Texto", color: "default" };
  return { text: "Doc", color: "default" };
}

interface AttachmentsManagerProps {
  /** Base del endpoint REST del módulo (ej. "/agenda-contactos"). */
  apiBase: string;
  /** Id del registro. null = aún no guardado → muestra aviso. */
  entityId: number | null;
  /** `true` mientras el modal padre esté abierto (gate del paste). */
  active: boolean;
  /** MIME accept (default: imágenes/videos/docs). */
  accept?: string;
  /** Texto del aviso cuando no hay id (registro nuevo sin guardar). */
  emptyHint?: string;
}

const AttachmentsManager: React.FC<AttachmentsManagerProps> = ({
  apiBase,
  entityId,
  active,
  accept = ATTACHMENTS_ACCEPT,
  emptyHint = "Guarda el registro antes de adjuntar archivos. Una vez creado, ábrelo para editar y podrás subir fotos y documentos.",
}) => {
  const [files, setFiles] = useState<AttachmentFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingKeys, setDeletingKeys] = useState<Set<string>>(new Set());
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewVideo, setPreviewVideo] = useState<AttachmentFile | null>(null);

  const reload = useCallback(async () => {
    if (!entityId) {
      setFiles([]);
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch<{ success: boolean; data: AttachmentFile[] }>(
        `${apiBase}/${entityId}/archivos`,
        { errorMsg: "Error al cargar los archivos" },
      );
      setFiles(res?.data ?? []);
    } finally {
      setLoading(false);
    }
  }, [apiBase, entityId]);

  useEffect(() => {
    if (active && entityId) {
      reload();
    } else if (!active) {
      setPreviewImage(null);
      setPreviewVideo(null);
    }
  }, [active, entityId, reload]);

  const handleUpload = useCallback(
    async (file: File) => {
      if (!entityId) {
        getMsg()?.warning("Guarda el registro antes de adjuntar archivos");
        return;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        getMsg()?.error(
          `"${file.name}" pesa ${formatBytes(file.size)} — el máximo es ${formatBytes(MAX_UPLOAD_BYTES)}.`,
        );
        return;
      }
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const res = await apiFetch<{ success: boolean; data: AttachmentFile }>(
          `${apiBase}/${entityId}/archivos`,
          { method: "POST", body: fd, errorMsg: `No se pudo subir "${file.name}"` },
        );
        if (res?.data) {
          setFiles((prev) => [...prev, res.data]);
          getMsg()?.success(`"${file.name}" subido correctamente`);
        }
      } finally {
        setUploading(false);
      }
    },
    [apiBase, entityId],
  );

  useClipboardPaste({
    active: active && !!entityId,
    onFile: handleUpload,
    accept,
  });

  const handleDelete = useCallback(
    async (file: AttachmentFile) => {
      if (!entityId) return;
      setDeletingKeys((prev) => new Set(prev).add(file.key));
      try {
        const res = await apiFetch<{ success: boolean; data: AttachmentFile[] }>(
          `${apiBase}/${entityId}/archivos?key=${encodeURIComponent(file.key)}`,
          { method: "DELETE", errorMsg: "No se pudo eliminar el archivo" },
        );
        if (res) setFiles(res.data ?? []);
      } finally {
        setDeletingKeys((prev) => {
          const next = new Set(prev);
          next.delete(file.key);
          return next;
        });
      }
    },
    [apiBase, entityId],
  );

  const openFile = useCallback((file: AttachmentFile) => {
    if (file.kind === "image") setPreviewImage(file.url);
    else if (file.kind === "video") setPreviewVideo(file);
    else window.open(file.url, "_blank", "noopener,noreferrer");
  }, []);

  const downloadOne = useCallback((file: AttachmentFile) => {
    // Forzamos descarga con ?d=1 (Content-Disposition: attachment). La URL ya
    // viene firmada; sólo agregamos el flag de descarga. Se comprueba con un HEAD antes del
    // clic (round 2026-10-28): el ancla navega la pestaña —front y API son orígenes distintos,
    // `download` se ignora— y un token vencido o un archivo borrado la reemplazaba por el JSON del error.
    const sep = file.url.includes("?") ? "&" : "?";
    void descargarVerificado(`${file.url}${sep}d=1`, file.originalName || "archivo");
  }, []);

  if (!entityId) {
    return <Alert type="info" showIcon message="Adjuntos" description={emptyHint} />;
  }

  return (
    <div>
      <DragDropUpload
        accept={accept}
        multiple
        loading={uploading}
        onFile={handleUpload}
        compact
        title="Arrastra fotos o documentos aquí para subirlos"
        hint="También: click para elegir, o Ctrl+V para pegar (capturas, archivos copiados)"
      />

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          margin: "12px 0 8px",
        }}
      >
        <Badge
          count={files.length}
          showZero
          style={{ backgroundColor: files.length ? "#1677ff" : "#bfbfbf" }}
        >
          <Text strong style={{ paddingRight: 8 }}>
            Archivos
          </Text>
        </Badge>
        {loading && <Spin size="small" />}
      </div>

      {files.length === 0 && !loading ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<Text type="secondary">Aún no hay archivos adjuntos.</Text>}
        />
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
            gap: 12,
          }}
        >
          {files.map((file) => {
            const isDeleting = deletingKeys.has(file.key);
            const { text: kindText, color: kindColor } = kindLabel(file);
            return (
              <div
                key={file.key}
                style={{
                  position: "relative",
                  border: "1px solid #f0f0f0",
                  borderRadius: 8,
                  overflow: "hidden",
                  background: "#fff",
                }}
              >
                <button
                  type="button"
                  onClick={() => openFile(file)}
                  title={`Abrir ${file.originalName}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: 110,
                    background: "#fafafa",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  {file.kind === "image" && file.thumbUrl ? (
                    <img
                      src={file.thumbUrl}
                      alt={file.originalName}
                      loading="lazy"
                      draggable={false}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : file.kind === "video" && file.thumbUrl ? (
                    <div style={{ position: "relative", width: "100%", height: "100%" }}>
                      <img
                        src={file.thumbUrl}
                        alt={file.originalName}
                        loading="lazy"
                        draggable={false}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                      <PlayCircleOutlined
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          fontSize: 32,
                          color: "#fff",
                          textShadow: "0 1px 4px rgba(0,0,0,.6)",
                        }}
                      />
                    </div>
                  ) : (
                    <FileIcon file={file} size={44} />
                  )}
                </button>

                <div style={{ padding: 8 }}>
                  <Tooltip title={file.originalName}>
                    <Text ellipsis style={{ display: "block", fontSize: 12, fontWeight: 500 }}>
                      {file.originalName}
                    </Text>
                  </Tooltip>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginTop: 4,
                    }}
                  >
                    <Tag color={kindColor} style={{ marginRight: 0 }}>
                      {kindText}
                    </Tag>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {formatBytes(file.size)}
                    </Text>
                  </div>
                  <Space size={4} style={{ marginTop: 6 }}>
                    <Tooltip title="Abrir / previsualizar">
                      <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => openFile(file)}
                      />
                    </Tooltip>
                    <Tooltip title="Descargar">
                      <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => downloadOne(file)}
                      />
                    </Tooltip>
                    <Popconfirm
                      title="¿Eliminar este archivo?"
                      description="No se puede recuperar."
                      okText="Eliminar"
                      cancelText="Cancelar"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => handleDelete(file)}
                    >
                      <Tooltip title="Eliminar">
                        <Button size="small" danger icon={<DeleteOutlined />} loading={isDeleting} />
                      </Tooltip>
                    </Popconfirm>
                  </Space>
                </div>

                {isDeleting && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "rgba(255,255,255,.6)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Spin />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {previewImage && (
        <AntImage
          src={previewImage}
          style={{ display: "none" }}
          preview={{
            visible: !!previewImage,
            src: previewImage,
            onVisibleChange: (vis) => {
              if (!vis) setPreviewImage(null);
            },
          }}
        />
      )}

      <AntModal
        open={!!previewVideo}
        onCancel={() => setPreviewVideo(null)}
        title={previewVideo?.originalName ?? "Video"}
        footer={null}
        width={900}
        style={{ top: 24 }}
        destroyOnClose
      >
        {previewVideo && (
          <VideoPlayer
            src={previewVideo.url}
            previewSrc={previewVideo.previewUrl}
            nombreArchivo={previewVideo.originalName}
            sizeBytes={previewVideo.size}
            previewSizeBytes={previewVideo.previewSize}
            altoMaximo="calc(100dvh - 220px)"
            autoPlay
            playsInline
          />
        )}
      </AntModal>
    </div>
  );
};

export default AttachmentsManager;
